import { loadScript, on, select, selectAll } from 'helpers/dom'
import debounce from 'lodash.debounce'

const selectors = {
  VIDEO_PLAYER_SELECTOR: 'video-player',
  VIDEO_THUMBNAIL_SELECTOR: '.js-video-player-thumbnail',
  VIDEO_THUMBNAIL_BUTTON_SELECTOR: '.js-video-player-thumbnail-button',
  HIDDEN_CLASS: 'opacity-0',
  PLAYING_CLASS: 'is-playing',
  LOADING_CLASS: 'is-loading',
  PAUSING_CLASS: 'is-pausing',
  VIDEO_TYPES: {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    MP4: 'mp4'
  },
  STATES: {
    PLAYING: 'playing',
    PAUSING: 'pausing',
    LOADING: 'loading'
  },
  DEBOUNCE_TIME: 200
}

export default class VideoPlayer extends HTMLElement {
  connectedCallback() {
    this.player = null
    this.type = this.getAttribute('data-video-type') || selectors.VIDEO_TYPES.MP4
    this.videos = selectAll(selectors.VIDEO_PLAYER_SELECTOR)
    this.thumbnailEl = null
    this.isLoaded = false
    this.src = this.getAttribute('src') || null
    this.status = {
      play: null,
      error: null
    }
    this.options = {
      autoplay: this.hasAttribute('autoplay'),
      controls: this.hasAttribute('controls'),
      playsinline: this.hasAttribute('playsinline'),
      muted: this.hasAttribute('muted'),
      loop: this.hasAttribute('loop'),
      origin: window.location.origin,
      thumbnail: this.getAttribute('data-thumbnail'),
      thumbnailMobile: this.getAttribute('data-thumbnail-mobile'),
      playIntersection: this.hasAttribute('data-play-intersection'),
      classButton: this.getAttribute('data-button-class'),
      lazyload: this.hasAttribute('lazyload'),
      singlePlay: this.hasAttribute('singlePlay')
    }
    this.element = document.createElement('div')

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
      this.appendChild(this.element)
    }

    if (!this.src) {
      this.status.error = 'No video src'
    }

    if (this.options.playIntersection) {
      this.initWhenVisible()
    }
    this.unload = this.unload.bind(this)
    this.play = this.play.bind(this)
    this.pause = this.pause.bind(this)
    this.initWhenVisible = this.initWhenVisible.bind(this)

    if (this.hasAttribute('auto-init')) {
      this.init()
    }

    on(
      'resize',
      () => {
        const thumbnailEl = select('.js-image-thumbnail')

        if (thumbnailEl) {
          thumbnailEl.src = this.getSrcThumbnail()
        }
      },
      window
    )

    if (this.options.lazyload) {
      this.lazyload()
    }

    if (!this.options.controls) {
      this.videoToggle()
    }
  }

  get getVideoID() {
    if (this.getAttribute('data-external-id')) return this.getAttribute('data-external-id')

    if (/youtu\.?be/.test(this.src)) {
      const patterns = [
        /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
        /\?v=([^#&?]{11})/, // ?v=<id>
        /&v=([^#&?]{11})/, // &v=<id>
        /embed\/([^#&?]{11})/, // embed/<id>
        /\/v\/([^#&?]{11})/ // /v/<id>
      ]

      for (let i = 0; i < patterns.length; ++i) {
        if (patterns[i].test(this.src)) {
          return patterns[i].exec(this.src)[1]
        }
      }
    }

    if (/^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.test(this.src)) {
      const parseURL = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.exec(this.src)

      return parseURL[5]
    }

    return null
  }

  getSrcThumbnail() {
    if (window.innerWidth < 768) {
      return this.options.thumbnailMobile ? this.options.thumbnailMobile : this.options.thumbnail
    }

    return this.options.thumbnail
  }

  initWhenVisible() {
    this.observeVisibility(this).then((isVisible) => {
      if (!this.isLoaded && isVisible) {
        if (this.options.autoplay) {
          !this.thumbnailEl && this.initThumbnail()
        }
        this.src && this.type && this.init()
        this.isLoaded = true
      }
    })
  }

  unload() {
    switch (this.type) {
      case 'youtube':
        this.player.clearVideo()
        break
      case 'vimeo':
        this.player.unload()
        break
    }
  }

  initThumbnail() {
    const html = `
      <div class="js-video-player-thumbnail video-player-thumbnail absolute left-0 top-0 w-full h-full bg-grey-500 flex items-center justify-center transition-opacity z-20">
        <img src="${this.getSrcThumbnail()}" loading="lazy" class="w-full h-full object-cover js-image-thumbnail" alt="thumbnail video">
        <button type="button" class="js-video-player-thumbnail-button rounded-full text-while absolute h-12 w-12 flex z-10 items-center justify-center ${this.options.classButton}" aria-label="play video">
          <div class="[.is-loading_&]:hidden [.is-pausing_&]:hidden">
            <svg viewBox="0 0 48 48" width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 42c9.941 0 18-8.059 18-18S33.941 6 24 6 6 14.059 6 24s8.059 18 18 18Z" fill="#FDFCFC"/>
              <path d="m30 24-9-6v12l9-6Z" fill="#1D1D1D"/>
            </svg>
          </div>
          <div class="hidden [.is-pausing_&]:block">
            <svg width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
              <g stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"><path d="M25 5h-4.5a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1H25a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1ZM11.5 5H7a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1h4.5a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1Z"/>
              </g>
            </svg>
          </div>
          <div class="hidden [.is-loading_&]:block">
            <span class="button-loading button-loading-large"></span>
          </div>
        </button>
      </div>
      `

    this.thumbnailEl = html
    this.insertAdjacentHTML('beforeend', this.thumbnailEl)

    const buttonEl = this.querySelector(selectors.VIDEO_THUMBNAIL_BUTTON_SELECTOR)

    if (this.options.autoplay) {
      this.setStatePlayer(selectors.STATES.LOADING)
    }
    buttonEl.addEventListener('click', () => {
      this.pauseAllSingleVideos()

      if (!this.player) {
        this.setStatePlayer(selectors.STATES.LOADING)

        if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
          this.setupYTPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.VIMEO) {
          this.setupVimeoPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.MP4) {
          this.setupMP4Player()
        }
      } else {
        if (!this.status.play) {
          this.onPlay()
        } else {
          this.onPause()
        }
      }
    })
  }

  initYoutubePlayer() {
    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      YT.ready(() => this.setupYTPlayer())
    }
  }

  initVimeoPlayer() {
    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      this.setupVimeoPlayer()
    }
  }

  initMP4Player() {
    if (this.querySelector('video')) return

    if (!this.options.autoplay && this.options.thumbnail) {
      !this.thumbnailEl && this.initThumbnail()
    } else {
      this.setupMP4Player()
    }
  }

  async initByType(type) {
    switch (type) {
      case selectors.VIDEO_TYPES.YOUTUBE:
        this.initYoutubePlayer()
        break
      case selectors.VIDEO_TYPES.VIMEO:
        this.initVimeoPlayer()
        break
      case selectors.VIDEO_TYPES.MP4:
        this.initMP4Player()
        break
    }
  }

  async loadPlayerAPIs(type) {
    let src = ''

    if (type === selectors.VIDEO_TYPES.YOUTUBE && !window.YT) {
      src = 'https://www.youtube.com/iframe_api'
    } else if (type === selectors.VIDEO_TYPES.VIMEO && !window.Vimeo) {
      src = 'https://player.vimeo.com/api/player.js'
    } else {
      return
    }

    return loadScript(src)
  }

  setupYTPlayer() {
    const options = {
      videoId: this.getVideoID,
      playerVars: {
        autoplay: 0,
        controls: this.options.controls ? 1 : 0,
        height: '100%',
        width: '100%',
        playsinline: this.options.playsinline ? 1 : 0,
        loop: this.options.loop ? 1 : 0
      },
      events: {
        onError: () => {
          this.status.error = new Error('player could not be built')
        },
        onStateChange: (e) => this._onYouTubeStateChange(e),
        onReady: (e) => this._onYoutubeReadyState(e)
      }
    }

    if (YT.loaded) {
      this.player = new window.YT.Player(this.element, options)
    }
  }

  setupVimeoPlayer() {
    if (this.player) return

    const options = {
      id: this.getVideoID,
      ...this.options
    }

    this.player = new window.Vimeo.Player(this, options)

    if (!this.options.autoplay) {
      this.player.ready().then(() => {
        this.play()
        this.setStatePlayer(selectors.STATES.PLAYING)
        this.displayThumbnail(false)
      })
    } else {
      this.player.ready().then(() => {
        this.play()
        this.displayThumbnail(false)
      })
    }

    this.player.on('play', async (data) => {
      const isPaused = await this.player.getPaused()
      const isEnded = await this.player.getEnded()
      const currentTime = data?.seconds || 0

      if ((!isPaused && !isEnded && currentTime > 0 && this.options.controls) || !this.options.controls) {
        this.onPlay()
      }
    })
    this.player.on('pause', () => this.onPause())
    this.player.on('ended', () => this.onEnd)
  }

  setupMP4Player() {
    this.videoEl = document.createElement('video')
    this.videoEl.src = this.src
    this.player = this.videoEl
    this.appendChild(this.videoEl)

    if (!this.options.autoplay) {
      this.play()
      this.setStatePlayer(selectors.STATES.PLAYING)
    }

    this.displayThumbnail(false)
    this.player.controls = this.options.controls
    this.player.autoplay = this.options.autoplay
    this.player.playsInline = this.options.playsinline
    this.player.muted = this.options.muted
    this.player.loop = this.options.loop

    this.player.addEventListener('play', () => {
      if (
        (!this.videoEl.paused && this.videoEl.currentTime > 0 && !this.videoEl.ended && this.options.controls) ||
        !this.options.controls
      ) {
        this.onPlay()
      }
    })
    this.player.addEventListener('pause', () => this.onPause())
    this.player.addEventListener('ended', () => this.onEnd())
  }

  play() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.playVideo()
      } else {
        this.player.play()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  pause() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.pauseVideo()
      } else {
        this.player.pause()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  onPlay() {
    this.play()
    this.status.play = true
    this.dispatchEvent(new CustomEvent('play'))
    this.setStatePlayer(selectors.STATES.PLAYING)
    this.displayThumbnail(false)
  }

  onPause() {
    this.pause()
    this.status.play = false
    this.dispatchEvent(new CustomEvent('pause'))
    this.setStatePlayer(selectors.STATES.PAUSING)
  }

  onEnd() {
    if (this.options.loop) {
      this.play()
    } else {
      this.status.play = false
      this.dispatchEvent(new CustomEvent('ended'))
      this.displayThumbnail(true)
      this.setStatePlayer(null)
    }
  }

  setStatePlayer(status) {
    switch (status) {
      case selectors.STATES.PLAYING:
        this.classList.add(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.PAUSING:
        this.classList.add(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.LOADING:
        this.classList.add(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        break
      default:
        this.classList.remove(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
    }
  }

  displayThumbnail(status) {
    const el = this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)

    if (!el) return

    if (status) {
      el.classList.remove(selectors.HIDDEN_CLASS)
      el.classList.add('visible')
      el.classList.remove('invisible')
    } else {
      el.classList.add(selectors.HIDDEN_CLASS)
      el.classList.remove('visible')
      el.classList.add('invisible')
    }
  }

  _onYouTubeStateChange(e) {
    const stateMap = {
      '-1': 'unstarted',
      0: 'ended',
      1: 'playing',
      2: 'pause',
      3: 'buffering',
      5: 'cued'
    }
    const state = stateMap[e.data.toString()]
    const eventMethodMap = {
      ended: this.onEnd,
      pause: this.onPause,
      playing: this.onPlay
    }

    if (eventMethodMap[state]) {
      const method = eventMethodMap[state]

      if (method) {
        method.call(this)
      }
    }
  }

  _onYoutubeReadyState() {
    if (!this.options.autoplay) {
      this.pause()
      this.setStatePlayer(selectors.STATES.PLAYING)
    }
    this.play()
    this.displayThumbnail(false)

    if (this.options.muted) {
      this.player.mute()
    }
  }

  lazyload() {
    const observer = new IntersectionObserver(
      debounce((entries) => {
        entries.forEach((entry) => {
          if (this.options.lazyload) {
            if (entry.isIntersecting) {
              this.initWhenVisible()
            } else if (this.options.autoplay) {
              this.play()
            } else {
              this.pause()
            }
          }
        })
      }, selectors.DEBOUNCE_TIME)
    )

    observer.observe(this)
  }

  observeVisibility(element) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting

          resolve(isVisible)
        })
      })

      observer.observe(element)
    })
  }

  videoToggle() {
    const html = `
      <div class="absolute left-0 top-0 w-full h-full z-5 flex justify-center items-center cursor-pointer js-video-toggle">
        <div class="hidden [.is-pausing_&]:block">
          <svg viewBox="0 0 48 48" width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 42c9.941 0 18-8.059 18-18S33.941 6 24 6 6 14.059 6 24s8.059 18 18 18Z" fill="#FDFCFC"/>
            <path d="m30 24-9-6v12l9-6Z" fill="#1D1D1D"/>
          </svg>
        </div>
      </div>
    `

    this.insertAdjacentHTML('beforeend', html)

    const buttonVideoToggle = select('.js-video-toggle', this)

    if (buttonVideoToggle) {
      on(
        'click',
        () => {
          if (!this.player) {
            throw new Error('player is undefined')
          }

          if (this.status.play) {
            this.onPause()
          } else {
            this.pauseAllSingleVideos()
            this.onPlay()
          }
        },
        buttonVideoToggle
      )
    }
  }

  pauseAllSingleVideos() {
    if (this.videos.length) {
      this.videos.forEach((video) => {
        if (video !== this && video.hasAttribute('single-play')) {
          video.onPause()
        }
      })
    }
  }

  async init() {
    this.type !== selectors.VIDEO_TYPES.MP4 && (await this.loadPlayerAPIs(this.type))
    this.initByType(this.type)
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer)
}
